import apiService from "@/lib/api";

// Admin Statistics — a diagnostic recruitment-analytics feature. Read-only,
// tenant-scoped endpoints (see server/app/api/v2/admin_statistics). Every method
// wraps apiService.getSecure in try/catch and returns response.data. Each
// endpoint accepts an optional { from, to } ISO-8601 window (all-time when
// omitted); the shape is stable so wiring a date picker later needs no changes.
const ADMIN_STATISTICS_BASE = "/api/v2/admin-statistics";

// Build the axios `params` object from an optional { from, to } range so callers
// can pass a window today and get all-time by default.
const rangeParams = (range) => {
  const params = {};
  if (range?.from) params.from = range.from;
  if (range?.to) params.to = range.to;
  return Object.keys(params).length ? { params } : undefined;
};

export const AdminStatisticsService = {
  // Hero KPI strip in one call — the diagnostic headline figures.
  getOverview: async (range) => {
    try {
      const response = await apiService.getSecure(
        `${ADMIN_STATISTICS_BASE}/overview`,
        rangeParams(range)
      );
      return response.data;
    } catch (error) {
      console.error("Error while fetching statistics overview:", error);
      throw error;
    }
  },

  // Conversion funnel with per-step drop-off (the centerpiece).
  getFunnel: async (range) => {
    try {
      const response = await apiService.getSecure(
        `${ADMIN_STATISTICS_BASE}/funnel`,
        rangeParams(range)
      );
      return response.data;
    } catch (error) {
      console.error("Error while fetching statistics funnel:", error);
      throw error;
    }
  },

  // Time-to-hire, applied->interviewed, and per-status dwell times.
  getVelocity: async (range) => {
    try {
      const response = await apiService.getSecure(
        `${ADMIN_STATISTICS_BASE}/velocity`,
        rangeParams(range)
      );
      return response.data;
    } catch (error) {
      console.error("Error while fetching statistics velocity:", error);
      throw error;
    }
  },

  // AI confidence band x human outcome — trust & calibration.
  getAiRecommendations: async (range) => {
    try {
      const response = await apiService.getSecure(
        `${ADMIN_STATISTICS_BASE}/ai-recommendations`,
        rangeParams(range)
      );
      return response.data;
    } catch (error) {
      console.error("Error while fetching statistics AI recommendations:", error);
      throw error;
    }
  },

  // Where candidates get rejected (grouped by the status they left).
  getRejections: async (range) => {
    try {
      const response = await apiService.getSecure(
        `${ADMIN_STATISTICS_BASE}/rejections`,
        rangeParams(range)
      );
      return response.data;
    } catch (error) {
      console.error("Error while fetching statistics rejections:", error);
      throw error;
    }
  },

  // Open jobs with no applications or no movement in 14+ days.
  getJobsAttention: async (range, limit = 20) => {
    try {
      const base = rangeParams(range) ?? { params: {} };
      const config = { params: { ...(base.params || {}), limit } };
      const response = await apiService.getSecure(
        `${ADMIN_STATISTICS_BASE}/jobs-attention`,
        config
      );
      return response.data;
    } catch (error) {
      console.error("Error while fetching statistics jobs-attention:", error);
      throw error;
    }
  },
};

export default AdminStatisticsService;
